// ---------------------------------------------------------------------------
// super_user (ทีมงานเจ้าของระบบ) — ข้อมูลข้ามทุกร้านของเว็บนี้
// ---------------------------------------------------------------------------
//   - เว็บนี้ใช้ชีตของตัวเอง super_user จึงเห็นเฉพาะร้านของเว็บนี้
//   - โหมด Google Sheets: list โดยไม่ส่ง org = ได้ทุกแถวทุกร้าน (มี org_id ติดมา)
//   - โหมด localStorage: ข้อมูลแยกตามร้าน ต้องวนอ่านทีละร้าน
//   - ทุกการแก้ไขบันทึกชื่อทีมงานไว้ (by / updatedBy) ตรวจย้อนหลังได้
// ---------------------------------------------------------------------------
import { ApiError } from "./apiClient";
import { gsEnabled } from "./gsClient";
import {
  organizationRepo, subscriptionRepo, addonPurchaseRepo, boostPointRepo, boostOrderRepo, customRequestRepo,
} from "../mock/repositories";
import { boostService } from "./addons";
import { getPlan, clampSeats, monthsOf } from "../config/plans";
import { addonModules } from "../config/addons";
import { API_ACCESS_KIND, CUSTOM_MIN_RATE, expectedQuoteAmount, validateQuote } from "../config/custom-request";

const ORG_FIELD = "org_id";

const newestFirst = (rows) => [...rows].sort((a, b) => new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0));

/** ทุกแถวของ collection ที่แยกตามร้าน พร้อม org_id */
const allOrgRows = async (repo, orgs) => {
  if (gsEnabled()) return repo.all();
  const lists = await Promise.all(orgs.map((org) => repo.all(org.id).then((rows) => rows.map((row) => ({ ...row, [ORG_FIELD]: org.id })))));
  return lists.flat();
};

const orgNameOf = (orgs) => {
  const map = orgs.reduce((acc, org) => ({ ...acc, [org.id]: org.name || org.id }), {});
  return (row) => row.orgName || map[row[ORG_FIELD]] || row[ORG_FIELD] || "-";
};

const nowIso = () => new Date().toISOString();

// สถานะคำขอที่ทีมงานเปลี่ยนได้ (จ่ายเงินต้องผ่าน QR เท่านั้น ทีมงานตั้ง paid เองไม่ได้)
export const SUPER_REQUEST_FLOW = {
  pending_quote: ["cancelled"],
  quoted: ["cancelled"],
  paid: ["in_progress", "done"],
  in_progress: ["done"],
  done: [],
  cancelled: [],
};

// คำขอเปิด API ไม่มีการเก็บเงิน — ทีมงานติดต่อกลับแล้วเปลี่ยนสถานะเองได้เลย
export const SUPER_API_FLOW = {
  pending_quote: ["in_progress", "done", "cancelled"],
  in_progress: ["done", "cancelled"],
  done: [],
  cancelled: [],
};

export const requestFlow = (row) => ((row?.kind === API_ACCESS_KIND ? SUPER_API_FLOW : SUPER_REQUEST_FLOW)[row?.status] || []);

export const SUPER_BOOST_FLOW = {
  pending: ["in_progress", "completed", "cancelled"],
  in_progress: ["completed", "cancelled"],
  completed: [],
  cancelled: [],
};

export const superService = {
  organizations: async () => newestFirst(await organizationRepo.all()),

  /**
   * ยอดขายทุกช่องทางของทุกร้าน (ใช้ออกใบแจ้งหนี้/สรุปยอด)
   * แถว: { id, date, orgId, orgName, category, item, amount, refNo, transactionId }
   */
  sales: async () => {
    const orgs = await organizationRepo.all();
    const nameOf = orgNameOf(orgs);
    const [subs, addons, points, requests] = await Promise.all([
      subscriptionRepo.all(),
      allOrgRows(addonPurchaseRepo, orgs),
      allOrgRows(boostPointRepo, orgs),
      allOrgRows(customRequestRepo, orgs),
    ]);

    const rows = [
      ...subs
        .filter((row) => row.status === "paid")
        .map((row) => ({
          id: `sub:${row.id}`, date: row.createdAt, orgId: row[ORG_FIELD], orgName: nameOf(row), category: "plan",
          item: `${row.planName || row.plan}${row.seats ? ` ${row.seats} User` : ""} (${row.months || monthsOf(row.cycle)} เดือน)`,
          amount: Number(row.amount) || 0, refNo: row.refNo || "", transactionId: row.transactionId || "",
        })),
      ...addons.map((row) => ({
        id: `addon:${row.id}`, date: row.createdAt, orgId: row[ORG_FIELD], orgName: nameOf(row), category: "addon",
        item: `${row.name || row.key}${row.days ? ` (${row.days} วัน)` : ""}`,
        amount: Number(row.amount) || 0, refNo: row.refNo || "", transactionId: row.transactionId || "",
      })),
      ...points
        .filter((row) => row.type === "topup")
        .map((row) => ({
          id: `points:${row.id}`, date: row.createdAt, orgId: row[ORG_FIELD], orgName: nameOf(row),
          category: row.wallet === "ai" ? "ai_credit" : "points",
          item: `${row.detail || "เติม point"} (${Number(row.points || 0).toLocaleString()} ${row.wallet === "ai" ? "เครดิต" : "point"})`,
          amount: Number(row.amount) || 0, refNo: row.refNo || "", transactionId: row.transactionId || "",
        })),
      ...requests
        .filter((row) => row.paidAt && ["paid", "in_progress", "done"].includes(row.status))
        .map((row) => ({
          id: `request:${row.id}`, date: row.paidAt, orgId: row[ORG_FIELD], orgName: nameOf(row),
          category: row.serviceKey ? "service" : "custom",
          item: `${row.title} (${row.requestNo || row.id})`,
          amount: Number(row.paidAmount ?? row.amount) || 0, refNo: row.refNo || "", transactionId: row.transactionId || "",
        })),
    ];
    return rows.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  },

  // ------------------------------ คำขอ / ใบเสนอราคา ------------------------------
  requests: async () => {
    const orgs = await organizationRepo.all();
    const nameOf = orgNameOf(orgs);
    return newestFirst(await allOrgRows(customRequestRepo, orgs)).map((row) => ({ ...row, orgName: nameOf(row) }));
  },

  /** ใส่/แก้ใบเสนอราคา — ได้เฉพาะก่อนจ่าย (pending_quote / quoted) · ยอด = mandays × rate */
  quote: async (row, { mandays, rate, note = "" }, by) => {
    const fresh = await customRequestRepo.get(row[ORG_FIELD], row.id);
    if (!fresh) throw new ApiError("ไม่พบคำขอ");
    if (fresh.kind === API_ACCESS_KIND) throw new ApiError("คำขอเปิด API ไม่มีใบเสนอราคา");
    if (!["pending_quote", "quoted"].includes(fresh.status)) throw new ApiError("คำขอนี้ชำระเงินแล้วหรือถูกยกเลิก แก้ใบเสนอราคาไม่ได้");
    const quote = { mandays: Number(mandays), ratePerManday: Number(rate), amount: expectedQuoteAmount(mandays, rate) };
    const check = validateQuote(quote);
    if (!check.ok) {
      throw new ApiError(check.reason === "rate" ? `อัตราต่อ manday ต้องไม่ต่ำกว่า ${CUSTOM_MIN_RATE.toLocaleString()} บาท` : "กรุณากรอกจำนวน manday และอัตราให้ถูกต้อง");
    }
    return customRequestRepo.update(row[ORG_FIELD], row.id, {
      ...quote, quoteNote: String(note || "").slice(0, 500), quotedAt: nowIso(), status: "quoted", quotedBy: by,
    });
  },

  setRequestStatus: async (row, status, by) => {
    const fresh = await customRequestRepo.get(row[ORG_FIELD], row.id);
    if (!fresh) throw new ApiError("ไม่พบคำขอ");
    if (!requestFlow(fresh).includes(status)) throw new ApiError("เปลี่ยนสถานะนี้ไม่ได้ (ข้อมูลอาจถูกแก้ไปแล้ว กรุณารีเฟรช)");
    return customRequestRepo.update(row[ORG_FIELD], row.id, { status, [`${status}At`]: nowIso(), updatedBy: by });
  },

  // ------------------------------ ออเดอร์ปั้ม ------------------------------
  boostOrders: async () => {
    const orgs = await organizationRepo.all();
    const nameOf = orgNameOf(orgs);
    return newestFirst(await allOrgRows(boostOrderRepo, orgs)).map((row) => ({ ...row, orgName: nameOf(row) }));
  },

  /** เปลี่ยนสถานะออเดอร์ปั้ม · ยกเลิก = คืน point ให้ร้าน (ไม่คืนซ้ำ) */
  setBoostStatus: async (row, status, by) => {
    const orgId = row[ORG_FIELD];
    const fresh = await boostOrderRepo.get(orgId, row.id);
    if (!fresh) throw new ApiError("ไม่พบออเดอร์");
    if (!(SUPER_BOOST_FLOW[fresh.status] || []).includes(status)) throw new ApiError("เปลี่ยนสถานะนี้ไม่ได้ (ข้อมูลอาจถูกแก้ไปแล้ว กรุณารีเฟรช)");
    await boostOrderRepo.update(orgId, row.id, { status, [`${status}At`]: nowIso(), updatedBy: by });
    if (status === "cancelled") await boostService.cancel(orgId, row.id);
    return boostOrderRepo.get(orgId, row.id);
  },

  // ------------------------------ แพ็กเกจของร้าน ------------------------------
  /**
   * ทีมงานเปิด/แก้แพ็กเกจให้ร้านโดยไม่ผ่าน QR (เช่น Ultimate หลังเซ็นสัญญา)
   * บันทึกแถวใน subscriptions (status = manual, amount = 0) ไว้ตรวจย้อนหลัง — ไม่นับเป็นยอดขาย
   */
  setPlan: async (organization, { planKey, seats, cycle = "monthly", expireAt, note = "" }, by) => {
    const plan = getPlan(planKey);
    if (!plan) throw new ApiError("ไม่พบแพ็กเกจที่เลือก");
    const expire = new Date(expireAt);
    if (!expireAt || Number.isNaN(expire.getTime())) throw new ApiError("กรุณาระบุวันหมดอายุ");
    if (!String(note || "").trim()) throw new ApiError("กรุณาระบุเหตุผล/เลขที่สัญญา");

    const current = (await organizationRepo.get(null, organization.id)) || organization;
    const planSeats = plan.perUser ? clampSeats(plan.key, seats) : "";
    const allowed = [...plan.modules, ...addonModules(current)];
    const keepModules = (current.enabledModules || []).filter((key) => allowed.includes(key));
    const now = nowIso();

    await subscriptionRepo.create(null, {
      org_id: current.id,
      orgName: current.name || "",
      plan: plan.key,
      planName: plan.name,
      seats: planSeats,
      cycle,
      months: 0,
      amount: 0,
      status: "manual",
      note: String(note).trim().slice(0, 500),
      by,
      startAt: now,
      expireAt: expire.toISOString(),
      createdAt: now,
    });

    return organizationRepo.update(null, current.id, {
      plan: plan.key,
      planCycle: cycle,
      planSeats,
      planStatus: "active",
      planStartAt: current.planStartAt || now,
      planExpireAt: expire.toISOString(),
      enabledModules: keepModules.length ? keepModules : plan.modules,
    });
  },

  /** ประวัติแพ็กเกจของร้าน (รวมที่ทีมงานเปิดให้) */
  planHistory: async (orgId) => newestFirst(await subscriptionRepo.all(null, (row) => row[ORG_FIELD] === orgId)),
};
